import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2 } from "lucide-react";
import { api } from "../api";
import PasswordInput from "../components/PasswordInput";

const inputCls =
  "w-full bg-[#12131f] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm outline-none focus:border-[#25D366] transition-colors";

// mode: "reset" | "invite"
const SetPassword = ({ mode, onAuthed }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const isInvite = mode === "invite";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      if (isInvite) {
        const data = await api.acceptInvite(token, password);
        localStorage.setItem("admin_token", data.access_token);
        if (onAuthed) await onAuthed();
        setLoading(false);
        navigate("/admin");
      } else {
        await api.reset(token, password);
        setLoading(false);
        setDone(true);
      }
    } catch (err) {
      setLoading(false);
      setError(err?.response?.data?.detail || "Something went wrong");
    }
  };

  if (!token) {
    return (
      <div className="dot-grid min-h-screen flex items-center justify-center px-5">
        <div className="w-full max-w-md bg-[#1b1d2b] border border-white/10 rounded-2xl p-8 text-center">
          <p className="text-white">Invalid link. No token provided.</p>
          <button onClick={() => navigate("/admin/login")} className="mt-4 text-[#25D366] hover:underline text-sm">Go to login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dot-grid min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md bg-[#1b1d2b] border border-white/10 rounded-2xl p-8 shadow-2xl fade-in-up">
        {done ? (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-[#25D366] mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl text-white">Password Reset</h1>
            <p className="text-white/60 text-sm mt-2">Your password has been updated successfully.</p>
            <button onClick={() => navigate("/admin/login")} className="mt-6 w-full bg-[#25D366] hover:bg-[#1fb757] text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
              Go to Login
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-7">
              <div className="w-14 h-14 mx-auto rounded-xl bg-[#25D366]/15 flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#25D366]" />
              </div>
              <h1 className="font-display font-bold text-2xl text-white">
                {isInvite ? "Set Your Password" : "Choose a New Password"}
              </h1>
              <p className="text-white/50 text-sm mt-1">
                {isInvite ? "Complete your agent account setup" : "Enter a new password for your account"}
              </p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-white/60 mb-1.5 block">New Password</label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
              </div>
              <div>
                <label className="text-xs font-medium text-white/60 mb-1.5 block">Confirm Password</label>
                <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button type="submit" disabled={loading} className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
                {loading ? "Saving..." : isInvite ? "Activate Account" : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SetPassword;
